import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type Commentaire from "models/Commentaire";
import { dateUtil, graphService as coreGraphService } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import graphService from "services/graphService";
import { search } from "jmespath";
import ChatIcon from "icons/ChatIcon";

interface CommandeCommentairesProps {
  commentaires: Array<Commentaire>;
}

async function getAgents(ids: Array<string>, setter: (users: Array<User>) => void): Promise<void> {
  const auteurs = await graphService.getAgentsByIds(ids);
  setter(auteurs);

  // TODO utiliser un cache/une batch request/un provider
  await Promise.all(
    auteurs.map(async (auteur, i) => {
      auteurs[i].avatar = await coreGraphService.getUserPhoto(auteur.id, "48x48");
    })
  );

  setter(auteurs);
}

function CommandeCommentaires({ commentaires }: Readonly<CommandeCommentairesProps>): ReactNode {
  const [auteurs, setAuteurs] = useState<Array<User>>([]);
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    if (commentaires.length !== 0) {
      const auteurIds: Array<string> = search(commentaires, "[*].auteur");

      if (auteurIds != null) {
        void getAgents(auteurIds, setAuteurs);
      }
    }
  }, [commentaires]);

  const sortedCommentaires = useMemo(
    () => commentaires.toSorted((c1, c2) => dateUtil.compareAsc(c1.date, c2.date)),
    [commentaires]
  );

  if (commentaires.length === 0) {
    return null;
  }

  return (
    <List sx={{ width: "100%", padding: 0 }}>
      {sortedCommentaires.map((commentaire) => {
        const auteur = auteurs.find((user) => user.id === commentaire.auteur);
        return (
          <ListItem alignItems="flex-start" sx={{ p: 0 }} key={commentaire.commentaireId}>
            <ListItemAvatar>
              <Avatar alt={auteur?.displayName} src={auteur?.avatar} />
            </ListItemAvatar>
            <Grid container size={12}>
              <Grid container size={12} spacing={0}>
                <Grid size={12}>
                  <Typography variant={isSmallScreen ? "body2" : "body1"}>
                    {auteur?.displayName}{" "}
                    <Typography component="span" variant="body2" color="grey">
                      {" - "}
                    </Typography>
                    <Typography component="span" variant="body2">
                      {dateUtil.format(commentaire.date, "dd/MM/yyyy")}
                    </Typography>
                  </Typography>
                </Grid>
                <Typography variant="body2" color="grey" sx={{ mt: "-2px", mb: 1 }}>
                  {auteur?.jobTitle}
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography
                  component="span"
                  variant={isSmallScreen ? "body2" : "body1"}
                  sx={{ whiteSpace: "break-spaces" }}>
                  {commentaire.value}
                </Typography>
              </Grid>
              <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
                <IconButton
                  color="primary"
                  href={`https://teams.microsoft.com/l/chat/0/0?users=${auteur?.mail}`}
                  target="_blank">
                  <Typography
                    sx={{
                      fontStyle: "italic",
                      pr: 1,
                      fontSize: isSmallScreen ? "0.7rem" : "0.8rem",
                    }}
                    component="span"
                    variant="caption">
                    Envoyer un message via Teams
                  </Typography>
                  <ChatIcon fontSize="small" sx={{ fontSize: "1.3rem" }} />
                </IconButton>
              </Grid>
            </Grid>
          </ListItem>
        );
      })}
    </List>
  );
}

export default CommandeCommentaires;
